import { ConceptStatus, ConceptStatusValidity } from '@fusion-advantage/backcompat'
import { Concept, ConceptActivation,  getConceptStatusValue } from '@fusion-advantage/formlib'
import { useCallback } from 'react'
import L1Select from '../l1/L1Select'
import { FusionColor } from '../../../theme/FusionStyle'
import { validityIndicatorProps } from '../../uiv2/Indicator/StatusIndicatorProps'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

export interface DateSelectPayload {
	date: string|null
}

interface L3DateSelectProps {
	concept: Concept
	conceptStatus: ConceptStatus|null
	validity: ConceptStatusValidity|null
	activation: ConceptActivation|null
	onChange: (e: DateSelectPayload|null) => void
	yearsToShow?: number
	startYear?: number
	endYear?: number
	showDays?: boolean
}

const DateSelectContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;
`

const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

const getDaysInMonth = (year: string, month: string): string[] => {
  if (!year || !month) return []

  const days = new Date(Number(year), Number(month), 0).getDate()

  return Array.from({ length: days }, (_, i) =>
    String(i + 1).padStart(2, '0')
  )
}

const generateYears = (numYears?: number, startYear?: number, endYear?: number): string[] => {
	let years: string[] = []

	if(startYear) {
		const currentYear = endYear || new Date().getFullYear()
		for(let year = startYear; year <= currentYear; year++) {
			years.unshift(String(year))
		}
	}
	else if(numYears && numYears > 0) {
		years = Array.from({ length: numYears }, (_, i) => String((endYear || new Date().getFullYear()) - i))
	}

	return years
}

export const getConceptDateValue = (conceptStatus: ConceptStatus|null): string[] => {
  let year = ''
  let month = ''
  let day = ''

  if(conceptStatus) {	
    const value = getConceptStatusValue(conceptStatus)

    if(value) {
      const dateString = String(value)
      year = dateString.slice(0, 4)
      month = dateString.slice(4, 6)
      day = dateString.slice(6, 8)

      if(month.includes("*")) month = ''
      if(year.includes("*")) year = ''
      if(day.includes("*")) day = ''
    }
  }

  return [month, year, day]
}

const L3DateSelect = ({ concept, conceptStatus, validity, activation, onChange, yearsToShow = 10, startYear, endYear, showDays }: L3DateSelectProps) => {
	const theme = useTheme()
	const disabled = activation === ConceptActivation.Disabled

	let validityColor: FusionColor|undefined
	let icon: IconProp|undefined
	if(validity) {
		const props = validityIndicatorProps(validity)
		validityColor = props.color
		icon = props.icon
	}

	const handleDateChange = useCallback((datePart: 'month'|'year'|'day', value: string) => {
		const [currMonth, currYear, currDay] = getConceptDateValue(conceptStatus)

		const month = datePart === 'month' ? value : currMonth
		const year = datePart === 'year' ? value : currYear
		const day = datePart === 'day' ? value : currDay || '01'

		const constructedDate =
			month && year
				? `${year}${month}${day}`
				: year
				? `${year}*101`
				: month
				? `****${month}01`
				: ''

		onChange({ date: constructedDate })
	}, [conceptStatus])

	const [month, year, day] = getConceptDateValue(conceptStatus)
	const years = generateYears(yearsToShow, startYear, endYear)
	const days = getDaysInMonth(year, month)

	return (
		<DateSelectContainer>
			<L1Select padding='0.5rem' value={month} disabled={disabled} borderColor={validityColor} onChange={(e) => handleDateChange('month', e.target.value)}>
				{
					months.map(m => (
						<option key={m} value={m}>{m}</option>
					))
				}
			</L1Select>
			<L1Select padding='0.5rem' value={year} disabled={disabled} borderColor={validityColor} onChange={(e) => handleDateChange('year', e.target.value)}>
				{
					years.map(y => (
						<option key={y} value={y}>{y}</option>
					))
				}
			</L1Select>
			{showDays && (
				<L1Select padding='0.5rem' value={day} disabled={disabled || !month || !year} borderColor={validityColor} onChange={(e) => handleDateChange('day', e.target.value)}>
					{days.map(d => (
						<option key={d} value={d}>{d}</option>
					))}
				</L1Select>
			)}
		</DateSelectContainer>
	)
}

export default L3DateSelect